import { useContext, useState, createContext, ReactNode, useEffect } from 'react'

import { useAppToast } from '../helpers'
import { UserService } from '../services/User/UserService'

type UserContextType = {
  id: string | null
  roles: string[]
  commercialConsultantId: number | null
  setRoles: (roles: string[]) => void
  fetchRoles: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export function UserProvider({ children }: { children: ReactNode }) {
  const [id, setId] = useState<string | null>(null)
  const [roles, setRoles] = useState<string[]>([])
  // Consultor comercial que o usuário logado representa (ABRA-140); null se nenhum.
  const [commercialConsultantId, setCommercialConsultantId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const toast = useAppToast()

  const fetchRoles = async () => {
    // Não tenta carregar roles se não houver token
    const token = localStorage.getItem('token')
    if (!token) {
      setLoading(false)
      return
    }

    const userService = new UserService()
    try {
      const meData = await userService.me()
      setRoles(meData.roles)
      setId(meData.id)
      setCommercialConsultantId(meData.commercial_consultant_id ?? null)
    } catch (error) {
      // Remove token inválido/expirado do localStorage
      localStorage.removeItem('token')
      setRoles([])
      setId(null)
      setCommercialConsultantId(null)
      toast.error('Erro ao carregar as permissões de usuário!', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Só tenta carregar roles se houver token (usuário logado)
    const token = localStorage.getItem('token')
    if (token) {
      fetchRoles()
    } else {
      setLoading(false)
    }
  }, [toast])

  return (
    <UserContext.Provider value={{ id, roles, commercialConsultantId, setRoles, fetchRoles }}>
      {!loading && children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser deve ser usado dentro de um UserProvider')
  }
  return context
}
